import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, Calendar, Globe } from 'lucide-react';
import { LANGUAGES, getLanguageFromPath, getEquivalentPath } from '../lib/i18n';

function FlagIcon({ lang, className = "w-5 h-3.5" }) {
  if (lang === 'da') {
    return (
      <svg viewBox="0 0 370 280" className={className} aria-hidden="true">
        <rect width="370" height="280" fill="#C8102E" />
        <rect width="40" height="280" x="120" fill="#ffffff" />
        <rect width="370" height="40" y="120" fill="#ffffff" />
      </svg>
    );
  }
  if (lang === 'en') {
    return (
      <svg viewBox="0 0 60 30" className={className} aria-hidden="true">
        <clipPath id="uk-flag-clip">
          <rect width="60" height="30" />
        </clipPath>
        <g clipPath="url(#uk-flag-clip)">
          <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="6" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
          <path d="M30,0 v30 M0,15 h60" stroke="#ffffff" strokeWidth="10" />
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
        </g>
      </svg>
    );
  }
  if (lang === 'de') {
    return (
      <svg viewBox="0 0 5 3" className={className} aria-hidden="true">
        <rect width="5" height="1" y="0" fill="#000000" />
        <rect width="5" height="1" y="1" fill="#DD0000" />
        <rect width="5" height="1" y="2" fill="#FFCE00" />
      </svg>
    );
  }
  return null;
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const currentLang = getLanguageFromPath(location.pathname);

  const NAV_DATA = {
    da: {
      homeHref: '/',
      contactLabel: 'Kontakt',
      contactHref: '/kontakt',
      calcLabel: 'Besparelsesberegner',
      calcHref: '/beregner',
      demoLabel: 'Book gratis demo',
      demoHref: '/gratis-demo',
      items: [
        {
          label: 'Maskinen',
          children: [
            { label: 'Produkt', href: '/produkt' },
            { label: 'Tilbehør', href: '/tilbehoer' },
            { label: 'Besparelsesberegner', href: '/beregner' }
          ]
        },
        {
          label: 'Finansiering & Leje',
          children: [
            { label: 'Leje', href: '/leje' },
            { label: 'Erhvervsleasing', href: '/erhvervsleasing' },
            { label: 'Finansiering til private', href: '/privatfinansiering' }
          ]
        },
        {
          label: 'Service & Demo',
          children: [
            { label: 'Service', href: '/service' },
            { label: 'Gratis demo', href: '/gratis-demo' }
          ]
        },
        {
          label: 'Om os',
          children: [
            { label: 'Om Danshaker', href: '/om-danshaker' },
            { label: 'Mød os her / events', href: '/moed-os-her' },
            { label: 'Nyheder', href: '/nyheder' },
            { label: 'Kundeudtalelser', href: '/kundeudtalelser' },
            { label: 'FAQ', href: '/faq' }
          ]
        }
      ]
    },
    en: {
      homeHref: '/en',
      contactLabel: 'Contact',
      contactHref: '/en/contact',
      calcLabel: 'Savings Calculator',
      calcHref: '/en/calculator',
      demoLabel: 'Book Free Demo',
      demoHref: '/en/free-demo',
      items: [
        {
          label: 'The Machine',
          children: [
            { label: 'Product', href: '/en/product' },
            { label: 'Accessories', href: '/en/accessories' },
            { label: 'Savings Calculator', href: '/en/calculator' }
          ]
        },
        {
          label: 'Financing & Rental',
          children: [
            { label: 'Rental', href: '/en/rent' },
            { label: 'Commercial Leasing', href: '/en/commercial-leasing' },
            { label: 'Private Financing', href: '/en/private-financing' }
          ]
        },
        {
          label: 'Service & Demo',
          children: [
            { label: 'Service', href: '/en/service' },
            { label: 'Free Demo', href: '/en/free-demo' }
          ]
        },
        {
          label: 'About Us',
          children: [
            { label: 'About Danshaker', href: '/en/about-danshaker' },
            { label: 'Meet Us / Events', href: '/en/meet-us' },
            { label: 'News', href: '/en/news' },
            { label: 'Reviews', href: '/en/reviews' },
            { label: 'FAQ', href: '/en/faq' }
          ]
        }
      ]
    },
    de: {
      homeHref: '/de',
      contactLabel: 'Kontakt',
      contactHref: '/de/kontakt',
      calcLabel: 'Ersparnisrechner',
      calcHref: '/de/sparrechner',
      demoLabel: 'Kostenlose Demo',
      demoHref: '/de/kostenlose-demo',
      items: [
        {
          label: 'Die Maschine',
          children: [
            { label: 'Produkt', href: '/de/produkt' },
            { label: 'Zubehör', href: '/de/zubehoer' },
            { label: 'Ersparnisrechner', href: '/de/sparrechner' }
          ]
        },
        {
          label: 'Finanzierung & Miete',
          children: [
            { label: 'Miete', href: '/de/miete' },
            { label: 'Gewerbeleasing', href: '/de/gewerbeleasing' },
            { label: 'Privatfinanzierung', href: '/de/privatfinanzierung' }
          ]
        },
        {
          label: 'Service & Demo',
          children: [
            { label: 'Service', href: '/de/service' },
            { label: 'Kostenlose Demo', href: '/de/kostenlose-demo' }
          ]
        },
        {
          label: 'Über uns',
          children: [
            { label: 'Über Danshaker', href: '/de/ueber-danshaker' },
            { label: 'Termine / Events', href: '/de/uns-treffen' },
            { label: 'Neuigkeiten', href: '/de/neuigkeiten' },
            { label: 'Kundenstimmen', href: '/de/kundenstimmen' },
            { label: 'FAQ', href: '/de/faq' }
          ]
        }
      ]
    }
  };

  const nav = NAV_DATA[currentLang] || NAV_DATA.da;
  const navigation = nav.items;

  const isLinkActive = (href) => location.pathname === href;
  const isDropdownActive = (item) => item.children.some(child => location.pathname === child.href);

  const toggleDropdown = (index) => {
    if (activeDropdown === index) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(index);
    }
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/90 backdrop-blur-lg">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-slate-100 px-4 py-2 text-xs text-slate-700 md:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <a href="https://www.facebook.com/Danshaker2020" target="_blank" rel="noopener noreferrer" aria-label="Følg Danshaker på Facebook" className="flex items-center gap-1.5 text-slate-700 hover:text-[#0E67A2] transition font-semibold">
              <i className="fa-brands fa-facebook text-sm text-[#0E67A2]" aria-hidden="true" />
              <span>Facebook</span>
            </a>
            <a href="https://www.instagram.com/danshaker_udmugningsmaskine/" target="_blank" rel="noopener noreferrer" aria-label="Følg Danshaker på Instagram" className="flex items-center gap-1.5 text-slate-700 hover:text-[#0E67A2] transition font-semibold">
              <i className="fa-brands fa-instagram text-sm bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af] bg-clip-text text-transparent" aria-hidden="true" />
              <span>Instagram</span>
            </a>
            <a href="https://www.youtube.com/@Danshaker-yp2eg" target="_blank" rel="noopener noreferrer" aria-label="Se Danshaker på YouTube" className="flex items-center gap-1.5 text-slate-700 hover:text-[#0E67A2] transition font-semibold">
              <i className="fa-brands fa-youtube text-sm text-[#FF0000]" aria-hidden="true" />
              <span>YouTube</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:25303589" className="flex items-center gap-1.5 transition hover:text-[#0E67A2] whitespace-nowrap font-semibold">
              <Phone className="h-3.5 w-3.5 text-[#0E67A2]" aria-hidden="true" />
              <span>+45 2530 3589</span>
            </a>
            <a href="mailto:info@danshaker.dk" className="flex items-center gap-1.5 transition hover:text-[#0E67A2] whitespace-nowrap font-semibold">
              <Mail className="h-3.5 w-3.5 text-[#0E67A2]" aria-hidden="true" />
              <span>info@danshaker.dk</span>
            </a>

            {/* Language Switcher in Top Bar */}
            <div className="flex items-center gap-2 border-l border-slate-300 pl-3 ml-1" aria-label="Language switcher">
              {LANGUAGES.map((lang) => {
                const isActive = currentLang === lang.code;
                return (
                  <Link
                    key={lang.code}
                    to={getEquivalentPath(location.pathname, lang.code)}
                    onClick={() => {
                      try { localStorage.setItem('danshaker_lang', lang.code); } catch {}
                    }}
                    title={lang.label}
                    aria-label={lang.label}
                    className={`inline-flex items-center justify-center rounded-xs overflow-hidden border transition-all duration-150 ${
                      isActive
                        ? 'ring-2 ring-[#0E67A2] ring-offset-1 border-[#0E67A2] shadow-xs scale-105 opacity-100'
                        : 'border-slate-300/80 opacity-65 hover:opacity-100 hover:scale-105 hover:border-slate-400'
                    }`}
                  >
                    <FlagIcon lang={lang.code} className="w-5 h-3.5 block object-cover" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="px-4 py-4 md:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          {/* Logo */}
          <Link to={nav.homeHref} className="flex items-center gap-3 transition hover:opacity-90">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/14d0f8cb-56b7-4a51-8379-6cfdf86af873.png"
              alt="Danshaker logo"
              width="220"
              height="80"
              fetchPriority="high"
              decoding="async"
              className="h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          {!isMobile && (
            <nav ref={dropdownRef} className="flex items-center gap-1" aria-label="Hovednavigation">
              {navigation.map((item, index) => (
                <div key={item.label} className="relative">
                  <button
                    onClick={() => toggleDropdown(index)}
                    aria-expanded={activeDropdown === index}
                    className={`flex items-center gap-1 rounded-xl px-4 py-2 text-sm font-semibold transition hover:bg-slate-100 ${
                      isDropdownActive(item)
                        ? 'text-[#0E67A2] bg-[#0E67A2]/5'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>

                  {/* Dropdown Menu */}
                  {activeDropdown === index && (
                    <div className="absolute left-0 mt-2 w-56 rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className={`block rounded-xl px-4 py-2.5 text-sm font-medium transition ${
                            isLinkActive(child.href)
                              ? 'bg-[#0E67A2]/10 text-[#0E67A2]'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <Link
                to={nav.contactHref}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition hover:bg-slate-100 ${
                  isLinkActive(nav.contactHref)
                    ? 'text-[#0E67A2] bg-[#0E67A2]/5'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {nav.contactLabel}
              </Link>
            </nav>
          )}

          {/* Action Button & Mobile Toggle */}
          <div className="flex items-center gap-4">
            {!isMobile && (
              <div className="flex items-center gap-3">
                <Link
                  to={nav.calcHref}
                  className="flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 shadow-md"
                >
                  <i className="fa-solid fa-calculator text-xs" aria-hidden="true" />
                  {nav.calcLabel}
                </Link>
                <Link
                  to={nav.demoHref}
                  className="flex items-center gap-2 rounded-xl bg-[#0E67A2] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md"
                >
                  <Calendar className="h-4 w-4" aria-hidden="true" />
                  {nav.demoLabel}
                </Link>
              </div>
            )}

            {isMobile && (
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="rounded-xl border border-slate-200 bg-slate-100 p-2.5 text-slate-800 transition hover:bg-slate-200 hover:text-slate-900 cursor-pointer"
                aria-label="Åbn menu"
              >
                {isOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu (< 1200px) */}
      {isMobile && isOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-6 shadow-2xl">
          <nav className="space-y-4" aria-label="Mobilnavigation">
            {/* Mobile Language Switcher */}
            <div className="pb-3 border-b border-slate-200">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Sprog / Language</p>
              <div className="flex items-center justify-end gap-2.5">
                {LANGUAGES.map((lang) => {
                  const isActive = currentLang === lang.code;
                  return (
                    <Link
                      key={lang.code}
                      to={getEquivalentPath(location.pathname, lang.code)}
                      onClick={() => {
                        try { localStorage.setItem('danshaker_lang', lang.code); } catch {}
                        setIsOpen(false);
                      }}
                      title={lang.label}
                      aria-label={lang.label}
                      className={`inline-flex items-center justify-center rounded-xs overflow-hidden border p-0.5 transition-all duration-150 ${
                        isActive
                          ? 'ring-2 ring-[#0E67A2] ring-offset-2 border-[#0E67A2] shadow-xs scale-105 opacity-100 bg-white'
                          : 'border-slate-300/80 opacity-75 hover:opacity-100 hover:scale-105 bg-white'
                      }`}
                    >
                      <FlagIcon lang={lang.code} className="w-6 h-4 block object-cover" />
                    </Link>
                  );
                })}
              </div>
            </div>

            {navigation.map((item, index) => (
              <div key={item.label} className="space-y-2">
                <button
                  onClick={() => toggleDropdown(index)}
                  className="flex w-full items-center justify-between rounded-xl bg-slate-100 px-4 py-3 text-left text-base font-bold text-slate-900"
                >
                  {item.label}
                  <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {activeDropdown === index && (
                  <div className="ml-4 border-l border-slate-200 pl-4 space-y-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className={`block py-2 text-sm font-semibold transition ${
                          isLinkActive(child.href) ? 'text-[#0E67A2]' : 'text-slate-500 hover:text-slate-900'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link
              to={nav.contactHref}
              className={`block rounded-xl bg-slate-100 px-4 py-3 text-base font-bold text-slate-900 ${
                isLinkActive(nav.contactHref) ? 'text-[#0E67A2] border-l-2 border-[#0E67A2] pl-3' : ''
              }`}
            >
              {nav.contactLabel}
            </Link>

            <div className="pt-4 space-y-3">
              <Link
                to={nav.calcHref}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-center font-bold text-white transition hover:bg-slate-800 shadow-md"
              >
                <i className="fa-solid fa-calculator text-sm" aria-hidden="true" />
                {nav.calcLabel}
              </Link>
              <Link
                to={nav.demoHref}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0E67A2] py-3 text-center font-bold text-white transition hover:bg-[#0E67A2]/90 shadow-md"
              >
                <Calendar className="h-5 w-5" aria-hidden="true" />
                {nav.demoLabel}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
